import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, CheckCircle, XCircle } from "lucide-react";
import { ApiService } from "../../services/api";
import checkPermission, { checkSuperAdmin } from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import { IlmekContentPack } from "../../types/ilmek";

// Paketin içerik (JSON) alanları — iskelette ham JSON olarak düzenlenir.
const CONTENT_FIELDS: Array<keyof IlmekContentPack> = [
  "ozet",
  "yanilgilar",
  "imge",
  "anahtarKelimeler",
  "ornek",
  "aktarimBaglamlari",
  "olcme",
  "gorevTohumu",
  "videoHavuzu",
];

export default function IlmekContentPackEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showAlert } = useModal();

  const [pack, setPack] = useState<IlmekContentPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [baslik, setBaslik] = useState("");
  const [ders, setDers] = useState("");
  const [sinif, setSinif] = useState<string>("");
  const [unite, setUnite] = useState("");
  const [contentJson, setContentJson] = useState("");

  const canApprove = checkSuperAdmin() || checkPermission("ilmek_content_pack_approve");

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await ApiService.getIlmekContentPack(id);
      setPack(data);
      setBaslik(data.baslik || "");
      setDers(data.ders || "");
      setSinif(data.sinif != null ? String(data.sinif) : "");
      setUnite(data.unite || "");
      const content: Record<string, any> = {};
      CONTENT_FIELDS.forEach((field) => {
        content[field as string] = (data as any)[field] ?? null;
      });
      setContentJson(JSON.stringify(content, null, 2));
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Paket yüklenemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!id) return;
    let parsedContent: Record<string, any> = {};
    try {
      parsedContent = contentJson.trim() ? JSON.parse(contentJson) : {};
    } catch (e) {
      await showAlert({
        title: "Geçersiz JSON",
        message: "İçerik alanı geçerli bir JSON olmalı.",
        type: "error",
      });
      return;
    }
    setSaving(true);
    try {
      await ApiService.updateIlmekContentPack(id, {
        baslik: baslik || undefined,
        ders: ders || undefined,
        sinif: sinif ? Number(sinif) : undefined,
        unite: unite || undefined,
        ...parsedContent,
      });
      await showAlert({ title: "Başarılı", message: "Paket kaydedildi.", type: "success" });
      load();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Kaydedilemedi.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [id, baslik, ders, sinif, unite, contentJson, showAlert, load]);

  const handleApprove = useCallback(
    async (action: "APPROVE" | "REJECT") => {
      if (!id) return;
      try {
        await ApiService.approveIlmekContentPack(id, action);
        await showAlert({
          title: "Başarılı",
          message: action === "APPROVE" ? "Paket onaylandı." : "Paket reddedildi.",
          type: "success",
        });
        load();
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: error?.message || "İşlem tamamlanamadı.",
          type: "error",
        });
      }
    },
    [id, showAlert, load]
  );

  const finalized = useMemo(
    () => pack?.onayDurumu === "ONAYLANDI" || pack?.onayDurumu === "REDDEDILDI",
    [pack]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!pack) {
    return <div className="p-6 text-sm text-gray-500">Paket bulunamadı.</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to="/ilmek" className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Paketler
      </Link>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4 flex items-start justify-between gap-3">
        <div>
          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
            {pack.kazanimKodu}
          </span>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">İçerik Paketini Düzenle</h1>
          <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">Durum: {pack.onayDurumu} · v{pack.surum}</p>
        </div>
        {canApprove && !finalized && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleApprove("APPROVE")}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
            >
              <CheckCircle className="w-4 h-4" /> Onayla
            </button>
            <button
              onClick={() => handleApprove("REJECT")}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-rose-700 bg-rose-50 hover:bg-rose-100"
            >
              <XCircle className="w-4 h-4" /> Reddet
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4 bg-white border border-gray-200 rounded-lg p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
            <input
              type="text"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ünite</label>
            <input
              type="text"
              value={unite}
              onChange={(e) => setUnite(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ders</label>
            <input
              type="text"
              value={ders}
              onChange={(e) => setDers(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sınıf</label>
            <input
              type="number"
              value={sinif}
              onChange={(e) => setSinif(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            İçerik (ozet, yanilgilar, imge, olcme, gorevTohumu, videoHavuzu …) — JSON
          </label>
          <textarea
            value={contentJson}
            onChange={(e) => setContentJson(e.target.value)}
            rows={18}
            spellCheck={false}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            İskelet sürümünde içerik alanları ham JSON olarak düzenlenir; ileride alan-bazlı editöre dönüşecek.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
